// ISD codes — single source of truth for phone country codes.
// To add/remove a country: edit this array only.
// India is first (pre-selected default). Rest are alphabetical.

export interface IsdCode {
  code:    string;  // e.g. "+91"
  country: string;  // e.g. "India"
  flag:    string;  // emoji flag e.g. "🇮🇳"
  digits:  number;  // expected local number length (used for validation)
}

export const ISD_CODES: IsdCode[] = [
  // ── India first (default) ──────────────────────────────────────────
  { code: "+91",  country: "India",          flag: "🇮🇳", digits: 10 },
  // ── Alphabetical ──────────────────────────────────────────────────
  { code: "+93",  country: "Afghanistan",    flag: "🇦🇫", digits: 9  },
  { code: "+61",  country: "Australia",      flag: "🇦🇺", digits: 9  },
  { code: "+973", country: "Bahrain",        flag: "🇧🇭", digits: 8  },
  { code: "+880", country: "Bangladesh",     flag: "🇧🇩", digits: 10 },
  { code: "+1",   country: "Canada",         flag: "🇨🇦", digits: 10 },
  { code: "+86",  country: "China",          flag: "🇨🇳", digits: 11 },
  { code: "+20",  country: "Egypt",          flag: "🇪🇬", digits: 10 },
  { code: "+251", country: "Ethiopia",       flag: "🇪🇹", digits: 9  },
  { code: "+33",  country: "France",         flag: "🇫🇷", digits: 9  },
  { code: "+49",  country: "Germany",        flag: "🇩🇪", digits: 10 },
  { code: "+852", country: "Hong Kong",      flag: "🇭🇰", digits: 8  },
  { code: "+353", country: "Ireland",        flag: "🇮🇪", digits: 9  },
  { code: "+972", country: "Israel",         flag: "🇮🇱", digits: 9  },
  { code: "+39",  country: "Italy",          flag: "🇮🇹", digits: 10 },
  { code: "+81",  country: "Japan",          flag: "🇯🇵", digits: 10 },
  { code: "+254", country: "Kenya",          flag: "🇰🇪", digits: 9  },
  { code: "+965", country: "Kuwait",         flag: "🇰🇼", digits: 8  },
  { code: "+60",  country: "Malaysia",       flag: "🇲🇾", digits: 9  },
  { code: "+960", country: "Maldives",       flag: "🇲🇻", digits: 7  },
  { code: "+230", country: "Mauritius",      flag: "🇲🇺", digits: 8  },
  { code: "+977", country: "Nepal",          flag: "🇳🇵", digits: 10 },
  { code: "+31",  country: "Netherlands",    flag: "🇳🇱", digits: 9  },
  { code: "+64",  country: "New Zealand",    flag: "🇳🇿", digits: 9  },
  { code: "+234", country: "Nigeria",        flag: "🇳🇬", digits: 10 },
  { code: "+968", country: "Oman",           flag: "🇴🇲", digits: 8  },
  { code: "+92",  country: "Pakistan",       flag: "🇵🇰", digits: 10 },
  { code: "+63",  country: "Philippines",    flag: "🇵🇭", digits: 10 },
  { code: "+974", country: "Qatar",          flag: "🇶🇦", digits: 8  },
  { code: "+966", country: "Saudi Arabia",   flag: "🇸🇦", digits: 9  },
  { code: "+65",  country: "Singapore",      flag: "🇸🇬", digits: 8  },
  { code: "+27",  country: "South Africa",   flag: "🇿🇦", digits: 9  },
  { code: "+82",  country: "South Korea",    flag: "🇰🇷", digits: 10 },
  { code: "+34",  country: "Spain",          flag: "🇪🇸", digits: 9  },
  { code: "+94",  country: "Sri Lanka",      flag: "🇱🇰", digits: 9  },
  { code: "+46",  country: "Sweden",         flag: "🇸🇪", digits: 9  },
  { code: "+255", country: "Tanzania",       flag: "🇹🇿", digits: 9  },
  { code: "+66",  country: "Thailand",       flag: "🇹🇭", digits: 9  },
  { code: "+971", country: "UAE",            flag: "🇦🇪", digits: 9  },
  { code: "+44",  country: "United Kingdom", flag: "🇬🇧", digits: 10 },
  { code: "+1",   country: "United States",  flag: "🇺🇸", digits: 10 },
  { code: "+256", country: "Uganda",         flag: "🇺🇬", digits: 9  },
  { code: "+84",  country: "Vietnam",        flag: "🇻🇳", digits: 9  },
  { code: "+260", country: "Zambia",         flag: "🇿🇲", digits: 9  },
  { code: "+263", country: "Zimbabwe",       flag: "🇿🇼", digits: 9  },
];

export const DEFAULT_ISD = ISD_CODES[0] as IsdCode; // India (always present)

/** Parse a stored phone string like "+919876543210" into { isd, local }. */
export function parsePhone(stored: string): { isd: IsdCode; local: string } {
  for (const isd of ISD_CODES) {
    if (stored.startsWith(isd.code)) {
      return { isd, local: stored.slice(isd.code.length) };
    }
  }
  return { isd: DEFAULT_ISD, local: stored };
}

/** Combine ISD code + local number into a stored string. */
export function formatPhone(isd: IsdCode, local: string): string {
  return `${isd.code}${local.replace(/\D/g, "")}`;
}
